import type { ReactNode } from 'react';
import { alpha, Box, Chip, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';

type BookPanelProps = {
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
  children: ReactNode;
  badge?: ReactNode;
};

const BookPanel = ({ icon, title, description, children, badge }: BookPanelProps) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        height: '100%',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.025)',
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        '&:focus-within': {
          borderColor: 'primary.main',
          boxShadow: `0 0 0 3px ${
            theme.palette.mode === 'dark' ? 'rgba(45, 152, 255, .14)' : 'rgba(0, 112, 204, .08)'
          }`,
        },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 36,
              height: 36,
              display: 'grid',
              flexShrink: 0,
              placeItems: 'center',
              color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.09),
              '& .MuiSvgIcon-root': { fontSize: 20 },
            }}>
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {description}
            </Typography>
          </Box>
        </Stack>
        {badge && <Chip size="small" label={badge} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }} />}
      </Stack>
      <Divider />
      <Box sx={{ p: { xs: 1.5, md: 2 }, minWidth: 0 }}>{children}</Box>
    </Paper>
  );
};

export default BookPanel;
